import React, { useCallback } from 'react';
import { getSelectorAttribute, vyTableSelectors } from '@vy/app-selectors';
import { VyTextInput } from '../../atoms/VyTextInput/VyTextInput';
import { VyDatePicker } from '../../date-picker/VyDatePicker';
import { VyCheckboxSelect, type SelectItem } from '../../molecules/selects';
import type { FilterInputType, FilterOption, FilterValue } from '../types';
import { DatePresetValueInput } from './DatePresetValueInput';

type FilterValueInputProps = {
  inputType: FilterInputType;
  value: FilterValue;
  options: FilterOption[];
  disabled: boolean;
  onValueChange: (value: FilterValue) => void;
};

export const FilterValueInput: React.FC<FilterValueInputProps> = React.memo(
  ({ inputType, value, options, disabled, onValueChange }) => {
    const handleTextValueChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value;

        if (inputType === 'number') {
          const numValue = parseFloat(inputValue);
          onValueChange(isNaN(numValue) ? 0 : numValue);
          return;
        }

        onValueChange(inputValue);
      },
      [onValueChange, inputType],
    );

    const handleDateChange = useCallback(
      (date: Date | null) => {
        onValueChange(date?.toISOString() ?? '');
      },
      [onValueChange],
    );

    const handleValueSelectChange = useCallback(
      (selectedItems: SelectItem[]) => {
        const selectedValues = selectedItems.map((item) => item.value);

        if (inputType === 'boolean' && selectedValues.length > 0) {
          onValueChange(selectedValues[0] === 'true');
          return;
        }

        onValueChange(selectedValues);
      },
      [onValueChange, inputType],
    );

    const getSelectedValueItems = (): SelectItem[] => {
      const selectedValues = Array.isArray(value) ? value.map(String) : [String(value)].filter(Boolean);
      return options
        .filter((option) => selectedValues.includes(option.value))
        .map((option) => ({ value: option.value, label: option.label }));
    };

    const getDisplayValue = () => {
      if (Array.isArray(value)) {
        return value.length > 0 ? String(value[0]) : '';
      }
      return value === 0 ? '0' : String(value || '');
    };

    const commonSx = {
      width: '170px',
      height: '36px',
      borderRadius: '4px',
      '& .MuiInputBase-input': {
        fontSize: '14px',
      },
      '&.MuiFormControl-root': {
        border: '1px solid rgba(0, 0, 0, 0.23)',
      },
      '& .MuiFilledInput-root': {
        backgroundColor: 'transparent !important',
      },
      '&.MuiFormControl-root:hover:not(:has(.Mui-disabled))': {
        border: '1px solid rgba(0, 0, 0, 0.87)',
      },
      '&:has(.Mui-focused)': {
        border: '2px solid #1976d2 !important',
      },
    };

    // MUI X v9 rewrote the date-picker field into PickersInputBase/PickersSectionList
    // components; their default (medium) padding makes the input ~56px tall, overflowing
    // the 36px filter row. Constrain the v9 picker classes to fit.
    const dateSx = {
      ...commonSx,
      '& .MuiPickersInputBase-root': {
        height: '34px',
        minHeight: '34px',
        fontSize: '14px',
      },
      '& .MuiPickersInputBase-sectionsContainer': {
        paddingTop: 0,
        paddingBottom: 0,
      },
      // The border is drawn on the FormControl (above); suppress the v9 picker's
      // own notched outline so hover/focus states don't render a second border.
      // v9 redraws the outline as a 2px blue border on hover/focus via a
      // higher-specificity rule, so each state has to be suppressed explicitly.
      '& .MuiPickersOutlinedInput-notchedOutline': {
        border: 'none',
      },
      '& .MuiPickersOutlinedInput-root:hover .MuiPickersOutlinedInput-notchedOutline, & .MuiPickersOutlinedInput-root.Mui-focused .MuiPickersOutlinedInput-notchedOutline':
        {
          border: 'none',
        },
    };

    const selectSx = {
      width: '170px',
      height: '36px !important',
      '& .MuiSelect-select': {
        height: '34px !important',
      },
      '& svg': {
        top: '6px !important',
      },
    };

    switch (inputType) {
      case 'date-preset':
        return (
          <DatePresetValueInput
            value={typeof value === 'string' ? value : ''}
            disabled={disabled}
            onValueChange={onValueChange}
          />
        );

      case 'select':
        return (
          <span {...getSelectorAttribute(vyTableSelectors.filterValueSelect)}>
            <VyCheckboxSelect
              selectItems={options.map((option) => ({ value: option.value, label: option.label }))}
              onSelectionChange={handleValueSelectChange}
              value={getSelectedValueItems()}
              placeholder="Select..."
              disabled={disabled}
              withChips
              maxChipsInInputArea={1}
              sx={selectSx}
            />
          </span>
        );

      case 'boolean':
        return (
          <span {...getSelectorAttribute(vyTableSelectors.filterValueSelect)}>
            <VyCheckboxSelect
              selectItems={options.map((option) => ({ value: option.value, label: option.label }))}
              onSelectionChange={handleValueSelectChange}
              value={getSelectedValueItems()}
              placeholder="Select..."
              disabled={disabled || (inputType === 'boolean' && options.length <= 1)}
              withChips
              maxChipsInInputArea={1}
              sx={selectSx}
            />
          </span>
        );

      case 'number':
        return (
          <VyTextInput
            {...getSelectorAttribute(vyTableSelectors.filterValueSelect)}
            type="number"
            size="small"
            value={getDisplayValue()}
            onChange={handleTextValueChange}
            inputProps={{ disabled }}
            placeholder="Enter value..."
            fullWidth
            sx={commonSx}
          />
        );

      case 'date':
        return (
          <span {...getSelectorAttribute(vyTableSelectors.filterValueSelect)}>
            <VyDatePicker
              value={getDisplayValue() ? new Date(getDisplayValue()) : null}
              onChange={handleDateChange}
              size="small"
              disabled={disabled}
              slotProps={{
                textField: {
                  sx: dateSx,
                },
              }}
            />
          </span>
        );

      default:
        return (
          <VyTextInput
            {...getSelectorAttribute(vyTableSelectors.filterValueSelect)}
            size="small"
            value={getDisplayValue()}
            onChange={handleTextValueChange}
            inputProps={{ disabled }}
            placeholder="Enter value..."
            sx={commonSx}
          />
        );
    }
  },
);
